import Phaser from 'phaser';
import { BIOMES } from '../../content/biomes';
import { BOOST_BY_ID, BOOSTS } from '../../content/boosts';
import { allowedMowers } from '../../content/levelSchema';
import { practiceFor, type PracticeItem } from '../../content/practice';
import { COSMETICS, type CosmeticDef } from '../../content/cosmetics';
import { ONLINE_ENABLED } from '../../platform/online';
import { MOWER_DEFS, MOWER_IDS, type MowerId } from '../../content/mowers';
import type { TileId } from '../../content/tiles';
import { TOOL_BY_ID, type ToolId } from '../../content/tools';
import {
  boostStatus,
  boostStock,
  buyBoost,
  buyMower,
  buyTool,
  buyUpgrade,
  canBuyMower,
  effectiveMower,
  engineInUse,
  isEquipped,
  ownedMowerIds,
  paintAllMowers,
  paintOnAllMowers,
  mowerStatus,
  buyToolUpgrade,
  mowerMastered,
  nextToolUpgrade,
  nextUpgradeCost,
  nextUpgradeOpensAfter,
  toolLevel,
  upgradedTool,
  SHOP_TOOLS,
  toggleCosmetic,
  toggleEquip,
  toolStatus,
  upgradeApplies,
  upgradeLevels,
  upgradeBlurb,
  upgradeLine,
  upgradeOpen,
  UPGRADES,
  type UpgradeKind,
  campaignProgress,
  setEngine,
  upgradeCosts,
} from '../../meta/economy';
import { markSeen, unseenUnlocks } from '../../meta/reveals';
import type { LevelSceneData } from './LevelScene';
import { wipeTo } from './WipeScene';
import { mowerNeedsFullGame } from '../../meta/unlock';
import type { Save } from '../../meta/save/schema';
import type { BoostId } from '../../sim/boosts';
import { Dir } from '../../sim/direction';
import { isPaint, ownMowerTexture, paintedMowerTexture, stripeFrame } from '../render/cosmeticArt';
import { ITEMS_KEY, itemFrame } from '../render/itemArt';
import { saves } from '../records';
import { SHARE_GOAL } from '../../meta/sharing';
import { bevelButton, clearScene, navyPanel, pager } from '../ui/chrome';
import { arriveSince, celebratePurchase } from '../ui/motion';
import { centerMowerIcon } from '../ui/mowerIcon';
import { drawHeader, drawTabBar, HEADER_H, TABBAR_H, type Header } from '../ui/navChrome';
import {
  itemPreview,
  mowerPreview,
  mowerShowcaseHeight,
  PREVIEW_HEIGHT,
  PREVIEW_PATCH,
  showcaseHeight,
  stripePreview,
  type DetailLine,
} from '../ui/showcase';
import { wrapLineCount } from '../ui/wrap';
import { label, UI } from '../ui/text';
import { drawCoin } from '../ui/widgets';

/**
 * The Garage (owner, 2026-10-05: the Shop folded in, so everything coins buy is in one place).
 * Four tabs: MOWERS (the picked mower's card with its upgrades listed under it, the mowers in a
 * row below), TOOLS, BOOSTS and STYLE (paint and stripes). Tap anything to look at it in the
 * showcase; its action (use, buy, upgrade, equip) is the wide button above the tab bar.
 */

type Tab = 'mowers' | 'tools' | 'boosts' | 'style';

/** What the showcase shows. */
type Focus =
  | { kind: 'mower'; id: MowerId }
  | { kind: 'upgrade'; id: UpgradeKind; mower: MowerId }
  | { kind: 'tool'; id: ToolId }
  | { kind: 'boost'; id: BoostId }
  | { kind: 'cosmetic'; id: string };

const TABS: { tab: Tab; name: string }[] = [
  { tab: 'mowers', name: 'MOWERS' },
  { tab: 'tools', name: 'TOOLS' },
  { tab: 'boosts', name: 'BOOSTS' },
  { tab: 'style', name: 'STYLE' },
];

/** Registry key: open the Garage on this tab and item (an unlock's "see it in the Garage"). */
export const REG_GARAGE_OPEN = 'garage.open';
export type GarageOpen =
  { tab: 'mowers'; id: MowerId } | { tab: 'tools'; id: ToolId } | { tab: 'boosts'; id: BoostId };

/**
 * Registry key: the lawn the results were going to when SEE IT IN THE GARAGE was tapped
 * (LevelSceneData). The Garage offers it in its header until a lawn starts (owner, 2026-10-10:
 * look at or buy the new thing, then straight back to mowing).
 */
export const REG_GARAGE_RESUME = 'garage.resume';

/** Plural names for what tools clear, for the showcase. */
const CLEARS_NAME: Partial<Record<TileId, string>> = {
  sticks: 'sticks',
  shrub: 'shrubs',
  hedge: 'hedges',
  rock: 'rocks',
  gnome: 'gnomes',
  birdBath: 'bird baths',
  tree: 'a tree',
};

/** A tool's effect in a few words (the showcase column is narrow). */
function toolSummary(id: ToolId): string[] {
  const def = TOOL_BY_ID[id];
  const parts: string[] = [];
  const cleared = def.clears.map((t) => CLEARS_NAME[t] ?? t);
  if (cleared.length > 0) parts.push(`Clears ${cleared.join(', ')}.`);
  if (def.flattens) parts.push('Fills molehills.');
  if (def.trims) parts.push(def.around ? 'Trims grass all round.' : 'Trims grass ahead.');
  if (def.shutsOff) parts.push('Turns sprinklers off.');
  if (def.sendsHome) parts.push('Sends critters home.');
  if (def.shoos) parts.push('Shoos critters.');
  return parts;
}

/** Mowed-tile frame to chip color, for the tiny stripe swatches. */
const STRIPE_SWATCH: Record<string, number> = {
  mowedN: 0x27702e,
  mowedS: 0x1d5a25,
  mowedE: 0x256c2c,
  mowedW: 0x1f5e27,
};

/** A mower whose lawn is cleared but which comes with the full game (not bought yet). */
const waitsForFullGame = (save: Readonly<Save>, id: MowerId): boolean =>
  !save.fullGame &&
  mowerNeedsFullGame(MOWER_DEFS[id].unlockLevel) &&
  campaignProgress(save) >= MOWER_DEFS[id].unlockLevel;

/** How far a finger must travel sideways to turn a page (as on the menu). */
const SWIPE_PAGE = 24;
const TABS_Y = HEADER_H + 4;
const CONTENT_Y = TABS_Y + 18;
/** The showcase picture's offset inside its panel (below the name line). */
const PICTURE_DY = 16;
/** TRY IT under a showcase picture: the panel keeps this much room below the picture. */
const TRY_H = 13;
const TRY_ROOM = PREVIEW_PATCH.height + 4 + TRY_H;
/** The action button's band above the tab bar. */
const ACTION_H = 32;
/** Mower chips under the card, and the grid chips of the other tabs. */
const MOWER_CHIP_H = 44;
const CHIP_H = 40;
const GRID_COLS = 4;

export class GarageScene extends Phaser.Scene {
  private tab: Tab = 'mowers';
  private focus: Focus | null = null;
  private message = '';
  /** The next draw is a fresh arrival (scene opened, tab switched): the chips pop in. */
  private entering = true;
  /** A purchase just went through: the next draw celebrates it (coins fly, stamp). */
  private purchase: { from: number; stamp: string } | null = null;
  /** Grid pages on show (short screens page them); -1: the focused item's page. */
  private page = -1;
  private upgradePage = 0;

  constructor() {
    super('Garage');
  }

  create(data?: { open?: GarageOpen }): void {
    this.tab = 'mowers';
    this.focus = null;
    this.message = '';
    this.entering = true;
    this.purchase = null;
    this.page = -1;
    this.upgradePage = 0;
    // What to show: asked for by a reveal (the registry), or the item a TRY IT lawn came from.
    const open = (this.registry.get(REG_GARAGE_OPEN) as GarageOpen | undefined) ?? data?.open;
    if (open) {
      this.registry.remove(REG_GARAGE_OPEN);
      this.tab = open.tab;
      this.focus =
        open.tab === 'mowers'
          ? { kind: 'mower', id: open.id }
          : open.tab === 'tools'
            ? { kind: 'tool', id: open.id }
            : { kind: 'boost', id: open.id };
    }
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    // Swipe sideways across a grid of chips to turn its page, as on the menu's zones (owner,
    // 2026-10-07).
    const onUp = (p: Phaser.Input.Pointer) => {
      const area = this.swipeArea;
      if (p.wasCanceled || !area || p.downY < area.top || p.downY > area.bottom) return;
      const dx = p.upX - p.downX;
      const dy = p.upY - p.downY;
      if (Math.abs(dx) < SWIPE_PAGE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      area.turn(dx < 0 ? 1 : -1);
    };
    this.input.on('pointerup', onUp);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.input.off('pointerup', onUp));
  }

  /** The chip grid on show, if it has pages (for swipes), and how to turn them. */
  private swipeArea: { top: number; bottom: number; turn: (by: number) => void } | null = null;

  private readonly redraw = (): void => {
    // A purchase's coins and stamp belong to the tab they were bought on.
    this.tweens.killAll();
    this.time.removeAllEvents();
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    this.swipeArea = null;
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    const resume = this.registry.get(REG_GARAGE_RESUME) as LevelSceneData | undefined;
    const header = drawHeader(this, resume ? '' : 'GARAGE', 'garage');
    if (resume) this.drawResume(resume, header);
    this.drawTabs(g, width);

    const focus = this.focus ?? this.defaultFocus();
    this.markLooked(focus);
    const actionTop = height - TABBAR_H - ACTION_H;
    const listBottom = actionTop - (this.message ? 14 : 2);
    let target: { x: number; y: number };
    switch (this.tab) {
      case 'mowers':
        target = this.drawMowersTab(g, width, listBottom, focus);
        break;
      case 'tools':
        target = this.drawToolsTab(g, width, listBottom, focus);
        break;
      case 'boosts':
        target = this.drawBoostsTab(g, width, listBottom, focus);
        break;
      case 'style':
        target = this.drawStyleTab(g, width, listBottom, focus);
        break;
    }

    if (this.message) {
      label(this, width / 2, actionTop - 12, this.message, 1, UI.accent).setOrigin(0.5, 0);
    }
    this.drawAction(focus, 6, actionTop + 2, width - 12);
    drawTabBar(this, 'garage');

    this.entering = false;
    if (this.purchase) this.celebrate(header, target);
  }

  /**
   * The way back to mowing, in place of the header's title: the lawn the results were going to,
   * played with the mower in use now (if that lawn allows it).
   */
  private drawResume(resume: LevelSceneData, header: Header): void {
    const n = resume.playlist?.kind === 'campaign' ? resume.playlist.index + 1 : null;
    const text = n !== null ? `MOW LAWN ${n} >` : 'MOW ON >';
    const w = Math.min(6 * text.length + 14, Math.floor(header.coin.x) - 16);
    bevelButton(
      this,
      3,
      1,
      w,
      HEADER_H - 3,
      text,
      () => {
        const save = saves().save;
        const pick = save.selectedMower;
        const ok =
          save.ownedMowers.includes(pick) && allowedMowers(resume.level.mowerRule).includes(pick);
        wipeTo(this, 'Level', { ...resume, mowerId: ok ? pick : resume.mowerId });
      },
      true,
    );
  }

  private celebrate(header: Header, target: { x: number; y: number }): void {
    const purchase = this.purchase!;
    this.purchase = null;
    celebratePurchase(this, {
      wallet: header.wallet,
      from: purchase.from,
      to: saves().save.coins,
      target,
      stamp: purchase.stamp,
    });
  }

  /**
   * What the player is looking at is no longer new: the focused mower, tool or boost, and the
   * upgrades on the mowers tab (the NEW badge and tags go once seen).
   */
  private markLooked(focus: Focus): void {
    const keys: string[] = [];
    if (focus.kind === 'mower' || focus.kind === 'tool' || focus.kind === 'boost') {
      keys.push(`${focus.kind}:${focus.id}`);
    }
    if (this.tab === 'mowers') keys.push(...this.unseen.filter((k) => k.startsWith('upgrade:')));
    if (keys.some((k) => this.unseen.includes(k))) saves().update((s) => markSeen(s, keys));
  }

  /** Unlocked things not yet looked at. */
  private get unseen(): string[] {
    return unseenUnlocks(saves().save);
  }

  /** A small NEW tag on a chip's top-left corner. */
  private newTag(x: number, y: number): void {
    const t = label(this, x + 2, y - 3, 'NEW', 1, 0xffffff).setDropShadow(0, 0, 0, 0);
    const g = this.add.graphics();
    g.fillStyle(UI.danger).fillRect(x, y - 4, t.width + 3, 9);
    this.children.bringToTop(t);
  }

  private drawTabs(g: Phaser.GameObjects.Graphics, width: number): void {
    const unseen = this.unseen;
    const tabW = Math.floor((width - 8 - 3 * (TABS.length - 1)) / TABS.length);
    TABS.forEach(({ tab, name }, i) => {
      const x = 4 + i * (tabW + 3);
      const on = tab === this.tab;
      g.fillStyle(on ? UI.panel : 0x2a2550).fillRect(x, TABS_Y, tabW, 14);
      g.lineStyle(1, on ? UI.accent : UI.panelHi).strokeRect(x + 0.5, TABS_Y + 0.5, tabW - 1, 13);
      label(this, x + tabW / 2, TABS_Y + 4, name, 1, on ? UI.accent : UI.dim).setOrigin(0.5, 0);
      // Something new on this tab: a red dot in its corner.
      const prefix = {
        mowers: ['mower:', 'upgrade:'],
        tools: ['tool:'],
        boosts: ['boost:'],
        style: [],
      }[tab];
      if (!on && unseen.some((k) => prefix.some((p) => k.startsWith(p)))) {
        g.fillStyle(UI.danger).fillRect(x + tabW - 5, TABS_Y + 1, 4, 4);
      }
      this.add
        .zone(x, TABS_Y, tabW, 14)
        .setOrigin(0)
        .setData('label', name)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => {
          this.tab = tab;
          this.focus = null;
          this.message = '';
          this.entering = true;
          this.page = -1;
          this.upgradePage = 0;
          this.redraw();
        });
    });
  }

  private defaultFocus(): Focus {
    const save = saves().save;
    switch (this.tab) {
      case 'mowers':
        return { kind: 'mower', id: save.selectedMower };
      case 'tools':
        return { kind: 'tool', id: SHOP_TOOLS[0]! };
      case 'boosts':
        return { kind: 'boost', id: BOOSTS[0]!.id };
      case 'style':
        return { kind: 'cosmetic', id: COSMETICS[0]!.id };
    }
  }

  private setFocus(focus: Focus): void {
    this.focus = focus;
    this.message = '';
    this.redraw();
  }

  /** A sprite sheet for a mower as the player has it: wearing its equipped paint, if any. */
  private mowerTexture(id: MowerId): string {
    return ownMowerTexture(this, id, saves().save);
  }

  // ── Mowers ───────────────────────────────────────────────────────────────────────────────

  /**
   * The mower card: name, the mower mowing its patch, its stats, and (once it is yours) its
   * upgrades listed under it. The mowers sit in a row of chips below; on a short screen arrows
   * on the card switch mower instead, and the upgrades page.
   */
  private drawMowersTab(
    g: Phaser.GameObjects.Graphics,
    width: number,
    bottom: number,
    focus: Focus,
  ): { x: number; y: number } {
    const save = saves().save;
    const id =
      focus.kind === 'upgrade'
        ? focus.mower
        : focus.kind === 'mower'
          ? focus.id
          : save.selectedMower;
    const owned = mowerStatus(save, id) === 'owned';
    const kinds = (Object.keys(UPGRADES) as UpgradeKind[]).filter(
      (k) => upgradeApplies(id, k) && upgradeOpen(save, k),
    );
    // The stats column's height for the wordiest mower, so the card does not jump.
    const stats = Math.max(
      PREVIEW_HEIGHT,
      TRY_ROOM,
      ...MOWER_IDS.map((m) => mowerShowcaseHeight(width - 20, effectiveMower(save, m))),
    );
    const upgradesY = CONTENT_Y + PICTURE_DY + stats + 6;
    const rowsTop = upgradesY + 11;
    const lines = owned ? kinds.length : 1;
    // The roomiest layout that fits: two-line rows and chips, one-line rows and chips, then
    // one-line rows with arrows on the card (and pages of upgrades if even those do not fit).
    const room = (rowH: number, chips: boolean) =>
      rowsTop + lines * rowH + 4 + (chips ? 6 + MOWER_CHIP_H : 0) <= bottom;
    const [rowH, chips] = room(20, true) ? [20, true] : room(11, true) ? [11, true] : [11, false];
    const perPage = Math.max(1, Math.floor((bottom - 4 - rowsTop) / rowH));
    const pages = owned ? Math.ceil(kinds.length / perPage) : 1;
    const page = Math.min(this.upgradePage, pages - 1);
    const shown = owned ? kinds.slice(page * perPage, (page + 1) * perPage) : [];
    const panelBottom = rowsTop + Math.max(1, shown.length || lines) * rowH + 4;
    navyPanel(g, 4, CONTENT_Y, width - 8, panelBottom - CONTENT_Y);

    const name = label(this, 10, CONTENT_Y + 6, MOWER_DEFS[id].name.toUpperCase(), 1, UI.accent);
    if (mowerMastered(save, id)) {
      // Every upgrade bought: the gold Master trim, and a tag beside the name.
      label(this, name.x + name.width + 6, CONTENT_Y + 6, 'MASTER', 1, UI.good);
    }
    if (!chips) {
      // Arrows on the name line switch mower.
      const i = MOWER_IDS.indexOf(id);
      for (const [x, text, by] of [
        [width - 30, '<', -1],
        [width - 14, '>', 1],
      ] as const) {
        label(this, x, CONTENT_Y + 6, text, 1, UI.text).setOrigin(0.5, 0);
        this.add
          .zone(x - 8, CONTENT_Y, 16, 16)
          .setOrigin(0)
          .setData('label', by < 0 ? 'PREV MOWER' : 'NEXT MOWER')
          .setInteractive({ useHandCursor: true })
          .on('pointerup', () => {
            this.upgradePage = 0;
            this.setFocus({
              kind: 'mower',
              id: MOWER_IDS[(i + by + MOWER_IDS.length) % MOWER_IDS.length]!,
            });
          });
      }
    }
    mowerPreview(this, 10, CONTENT_Y + PICTURE_DY, width - 20, id, {
      stats: effectiveMower(save, id),
      locked: mowerStatus(save, id) === 'locked',
      texture: this.mowerTexture(id),
    });
    this.tryItButton({ kind: 'mower', id }, { tab: 'mowers', id });
    // The mower itself: back from an upgrade to the mower (and its row of chips).
    if (focus.kind === 'upgrade') {
      this.add
        .zone(10, CONTENT_Y + PICTURE_DY, PREVIEW_PATCH.width, PREVIEW_PATCH.height)
        .setOrigin(0)
        .setInteractive({ useHandCursor: true })
        .on('pointerup', () => this.setFocus({ kind: 'mower', id }));
    }

    // Upgrades, under a rule.
    const head = label(this, 12, upgradesY, 'UPGRADES', 1, UI.dim);
    const ruleEnd = width - 10 - (pages > 1 ? 50 : 0);
    g.fillStyle(UI.panelHi).fillRect(16 + head.width, upgradesY + 3, ruleEnd - 16 - head.width, 1);
    if (owned) {
      pager(this, width - 10, upgradesY, page, pages, (p) => {
        this.upgradePage = p;
        this.redraw();
      });
      shown.forEach((kind, n) =>
        this.drawUpgradeRow(g, id, kind, rowsTop + n * rowH, rowH, width, focus),
      );
    } else {
      const status = mowerStatus(save, id);
      label(
        this,
        12,
        rowsTop + 1,
        status === 'locked'
          ? waitsForFullGame(save, id)
            ? 'Comes with the full game.'
            : `Opens after lawn ${MOWER_DEFS[id].unlockLevel}.`
          : 'Buy it to upgrade it.',
        1,
        UI.dim,
      );
    }

    // The upgrade picked: what it does, and what the next level brings. Under the mower chips
    // where there is room, else in their place (tap the mower, or the upgrade again, for them).
    const textW = width - 24;
    const say =
      focus.kind === 'upgrade' && owned
        ? [
            { text: upgradeBlurb(focus.id, id), color: UI.text },
            { text: upgradeLine(save, id, focus.id).text, color: UI.dim },
          ]
        : [];
    const sayH = say.reduce((h, l) => h + wrapLineCount(l.text, textW) * 10 + 2, 0);
    const chipsToo = chips && panelBottom + 6 + MOWER_CHIP_H + 6 + sayH <= bottom;
    if (chips && (say.length === 0 || chipsToo)) this.drawMowerChips(panelBottom + 6, width, focus);
    if (say.length > 0) {
      let y = panelBottom + 6 + (chipsToo ? MOWER_CHIP_H + 6 : 0);
      for (const line of say) {
        const h = wrapLineCount(line.text, textW) * 10;
        if (y + h - 3 > bottom) break;
        label(this, width / 2, y, line.text, 1, line.color)
          .setOrigin(0.5, 0)
          .setMaxWidth(textW)
          .setCenterAlign();
        y += h + 2;
      }
    }
    return {
      x: 10 + PREVIEW_PATCH.width / 2,
      y: CONTENT_Y + PICTURE_DY + PREVIEW_PATCH.height / 2,
    };
  }

  /** One upgrade under the mower card: icon, name, what the next level does, price or MAX. */
  private drawUpgradeRow(
    g: Phaser.GameObjects.Graphics,
    mower: MowerId,
    kind: UpgradeKind,
    y: number,
    h: number,
    width: number,
    focus: Focus,
  ): void {
    const save = saves().save;
    const line = upgradeLine(save, mower, kind);
    const cost = nextUpgradeCost(save, mower, kind);
    const on = focus.kind === 'upgrade' && focus.id === kind;
    if (on) g.lineStyle(1, UI.accent).strokeRect(8.5, y - 1.5, width - 17, h);
    const tall = h >= 20;
    if (tall) this.add.image(12, y + 1, ITEMS_KEY, itemFrame(kind)).setOrigin(0);
    const tx = tall ? 31 : 12;
    label(this, tx, y + 1, UPGRADES[kind].name, 1, UI.text);
    if (tall) label(this, tx, y + 10, line.text, 1, line.maxed ? UI.good : UI.dim);
    const priceY = tall ? y + 5 : y + 1;
    const opensAfter = nextUpgradeOpensAfter(save, mower, kind);
    if (opensAfter !== null) {
      // The next level opens with a later zone.
      label(this, width - 12, priceY, `L${opensAfter}`, 1, UI.dim).setOrigin(1, 0);
    } else if (cost === null) {
      label(this, width - 12, priceY, 'MAX', 1, UI.good).setOrigin(1, 0);
    } else {
      const price = label(
        this,
        width - 12,
        priceY,
        String(cost),
        1,
        save.coins >= cost ? UI.accent : UI.dim,
      ).setOrigin(1, 0);
      drawCoin(this.add.graphics(), Math.round(price.x - price.width - 10), priceY);
    }
    this.add
      .zone(8, y - 1, width - 16, h)
      .setOrigin(0)
      .setData('label', UPGRADES[kind].name)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () =>
        // Tapping the picked upgrade again goes back to the mower.
        this.setFocus(on ? { kind: 'mower', id: mower } : { kind: 'upgrade', id: kind, mower }),
      );
  }

  /** The mowers in a row: sprite plus ON / USE / price / LOCK. */
  private drawMowerChips(top: number, width: number, focus: Focus): void {
    const save = saves().save;
    const gap = 3;
    const shownId =
      focus.kind === 'upgrade' ? focus.mower : focus.kind === 'mower' ? focus.id : null;
    const unseen = this.unseen;
    const w = Math.floor((width - 8 - gap * (MOWER_IDS.length - 1)) / MOWER_IDS.length);
    MOWER_IDS.forEach((id, i) => {
      const mark = this.children.length;
      const x = 4 + i * (w + gap);
      const status = mowerStatus(save, id);
      const tag =
        save.selectedMower === id
          ? 'ON'
          : status === 'owned'
            ? 'USE'
            : status === 'buyable'
              ? String(MOWER_DEFS[id].price)
              : 'LOCK';
      this.chip(
        x,
        top,
        w,
        MOWER_CHIP_H,
        shownId === id,
        status === 'locked',
        MOWER_DEFS[id].name,
        () => {
          this.upgradePage = 0;
          this.setFocus({ kind: 'mower', id });
        },
      );
      const img = this.add.image(x + w / 2, top + 16, this.mowerTexture(id), Dir.E);
      img.setScale(Math.min(1, (w - 2) / img.width)); // the Fairway Mower is wider than a chip
      centerMowerIcon(img, id);
      if (status === 'locked')
        img.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.8);
      label(this, x + w / 2, top + 33, tag, 1, status === 'buyable' ? UI.accent : UI.dim).setOrigin(
        0.5,
        0,
      );
      if (unseen.includes(`mower:${id}`)) this.newTag(x, top);
      this.popIn(mark, i);
    });
  }

  // ── Tools, boosts, style ─────────────────────────────────────────────────────────────────

  /** A tool's showcase text. */
  private toolLines(id: ToolId): DetailLine[] {
    const save = saves().save;
    const level = toolLevel(save, id);
    const def = upgradedTool(TOOL_BY_ID[id], level);
    const lines: DetailLine[] = toolSummary(id).map((text) => ({ text }));
    lines.push({ text: `Reach ${def.range}  x${def.charges}`, color: UI.dim });
    // Its upgrades: how many, and what the next one does.
    const next = TOOL_BY_ID[id].upgrades[level];
    lines.push({
      text: next ? `Next: ${next.label}` : `Upgraded ${level}/${TOOL_BY_ID[id].upgrades.length}`,
      color: next ? UI.dim : UI.good,
    });
    return lines;
  }

  /**
   * A cosmetic's showcase text: what it is, and how it is won (owner, 2026-11-04: still shown once
   * it is, so a player can see how they got it).
   */
  private cosmeticLines(c: CosmeticDef): DetailLine[] {
    const save = saves().save;
    const lines: DetailLine[] = [
      {
        text:
          c.slot === 'paint'
            ? `Paint for your ${MOWER_DEFS[save.selectedMower].name}.`
            : 'Stripes for every lawn.',
      },
    ];
    lines.push({ text: cosmeticSource(c, save.cosmetics.owned.includes(c.id)), color: UI.dim });
    return lines;
  }

  /**
   * The showcase panel at the top of the tools, boosts and style tabs: a name, a right-hand tag,
   * and a picture with text beside it; as tall as the tab's wordiest item. Returns its bottom.
   */
  private showcasePanel(
    g: Phaser.GameObjects.Graphics,
    width: number,
    name: string,
    tag: { text: string; color: number } | null,
    allLines: DetailLine[][],
  ): number {
    // Room under the picture for TRY IT (every tool and boost has a practice lawn).
    const h = Math.max(TRY_ROOM, ...allLines.map((l) => showcaseHeight(width - 20, l)));
    const bottom = CONTENT_Y + PICTURE_DY + h + 6;
    navyPanel(g, 4, CONTENT_Y, width - 8, bottom - CONTENT_Y);
    label(this, 10, CONTENT_Y + 6, name, 1, UI.accent);
    if (tag) label(this, width - 10, CONTENT_Y + 6, tag.text, 1, tag.color).setOrigin(1, 0);
    return bottom;
  }

  /**
   * A grid of chips under a heading, a page at a time where it does not fit. `draw` fills chip
   * `i` (index into `ids`) at (x, y, w).
   */
  private grid<T>(
    top: number,
    bottom: number,
    width: number,
    heading: string,
    ids: readonly T[],
    focusIndex: number,
    draw: (id: T, i: number, x: number, y: number, w: number) => void,
  ): number {
    const gap = 4;
    const w = Math.floor((width - 8 - gap * (GRID_COLS - 1)) / GRID_COLS);
    const perPage = Math.max(1, Math.floor((bottom - top - 12 + gap) / (CHIP_H + gap))) * GRID_COLS;
    const pages = Math.ceil(ids.length / perPage);
    if (this.page < 0) this.page = Math.floor(Math.max(0, focusIndex) / perPage);
    const page = Math.min(this.page, pages - 1);
    const named =
      heading.length * 6 <= width - 12 - (pages > 1 ? 48 : 0)
        ? heading
        : (heading.split(':')[1]?.trim() ?? heading);
    label(this, 6, top, named, 1, UI.dim);
    const turnTo = (p: number) => {
      this.page = p;
      this.entering = true;
      this.redraw();
    };
    pager(this, width - 6, top, page, pages, turnTo);
    if (pages > 1) {
      this.swipeArea = {
        top,
        bottom,
        turn: (by) => turnTo((page + by + pages) % pages),
      };
    }
    const shown = ids.slice(page * perPage, (page + 1) * perPage);
    shown.forEach((id, n) => {
      const mark = this.children.length;
      draw(
        id,
        page * perPage + n,
        4 + (n % GRID_COLS) * (w + gap),
        top + 12 + Math.floor(n / GRID_COLS) * (CHIP_H + gap),
        w,
      );
      this.popIn(mark, n);
    });
    return top + 12 + Math.ceil(shown.length / GRID_COLS) * (CHIP_H + gap);
  }

  private drawToolsTab(
    g: Phaser.GameObjects.Graphics,
    width: number,
    bottom: number,
    focus: Focus,
  ): { x: number; y: number } {
    const save = saves().save;
    const id = focus.kind === 'tool' ? focus.id : SHOP_TOOLS[0]!;
    const status = toolStatus(save, id);
    const panelBottom = this.showcasePanel(
      g,
      width,
      TOOL_BY_ID[id].name.toUpperCase(),
      status === 'owned'
        ? save.tools.equipped.includes(id)
          ? { text: 'ON', color: UI.good }
          : { text: 'OWNED', color: UI.good }
        : null,
      SHOP_TOOLS.map((t) => this.toolLines(t)),
    );
    itemPreview(
      this,
      10,
      CONTENT_Y + PICTURE_DY,
      width - 20,
      id,
      this.toolLines(id),
      status === 'locked',
    );
    this.tryItButton({ kind: 'tool', id }, { tab: 'tools', id });
    const mower = MOWER_DEFS[save.selectedMower];
    const slots = `${mower.toolSlots} SLOT${mower.toolSlots > 1 ? 'S' : ''}`;
    this.grid(
      panelBottom + 8,
      bottom,
      width,
      `${mower.name.toUpperCase()}: ${slots}`,
      SHOP_TOOLS,
      SHOP_TOOLS.indexOf(id),
      (t, _i, x, y, w) => {
        const st = toolStatus(save, t);
        const equipped = save.tools.equipped.includes(t);
        const tag =
          st === 'owned'
            ? equipped
              ? 'ON'
              : 'OWN'
            : st === 'buyable'
              ? String(TOOL_BY_ID[t].price)
              : `L${TOOL_BY_ID[t].unlockLevel}`;
        this.chip(x, y, w, CHIP_H, t === id, st === 'locked', TOOL_BY_ID[t].name, () =>
          this.setFocus({ kind: 'tool', id: t }),
        );
        const icon = this.add.image(x + w / 2, y + 13, ITEMS_KEY, itemFrame(t));
        if (st === 'locked')
          icon.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.8);
        label(
          this,
          x + w / 2,
          y + 28,
          tag,
          1,
          equipped ? UI.good : st === 'buyable' ? UI.accent : UI.dim,
        ).setOrigin(0.5, 0);
        if (this.unseen.includes(`tool:${t}`)) this.newTag(x, y);
      },
    );
    return {
      x: 10 + PREVIEW_PATCH.width / 2,
      y: CONTENT_Y + PICTURE_DY + PREVIEW_PATCH.height / 2,
    };
  }

  private drawBoostsTab(
    g: Phaser.GameObjects.Graphics,
    width: number,
    bottom: number,
    focus: Focus,
  ): { x: number; y: number } {
    const save = saves().save;
    const id = focus.kind === 'boost' ? focus.id : BOOSTS[0]!.id;
    const def = BOOST_BY_ID[id];
    const stock = boostStock(save, id);
    const panelBottom = this.showcasePanel(
      g,
      width,
      def.name.toUpperCase(),
      { text: `x${stock}`, color: stock > 0 ? UI.good : UI.dim },
      BOOSTS.map((b) => [{ text: b.blurb }]),
    );
    itemPreview(
      this,
      10,
      CONTENT_Y + PICTURE_DY,
      width - 20,
      id,
      [{ text: def.blurb }],
      boostStatus(save, id) === 'locked',
    );
    this.tryItButton({ kind: 'boost', id }, { tab: 'boosts', id });
    const ids = BOOSTS.map((b) => b.id);
    this.grid(
      panelBottom + 8,
      bottom,
      width,
      'ONE USE EACH, ONCE PER LAWN',
      ids,
      ids.indexOf(id),
      (b, _i, x, y, w) => {
        const st = boostStatus(save, b);
        this.chip(x, y, w, CHIP_H, b === id, st === 'locked', BOOST_BY_ID[b].name, () =>
          this.setFocus({ kind: 'boost', id: b }),
        );
        // Low enough to clear the stock count in the corner.
        const icon = this.add.image(x + w / 2, y + 17, ITEMS_KEY, itemFrame(b));
        if (st === 'locked')
          icon.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.8);
        const have = boostStock(save, b);
        if (have > 0) label(this, x + w - 4, y + 3, `x${have}`, 1, UI.good).setOrigin(1, 0);
        const price = BOOST_BY_ID[b].price;
        label(
          this,
          x + w / 2,
          y + 29,
          st === 'locked' ? `L${BOOST_BY_ID[b].unlockLevel}` : String(price),
          1,
          st === 'locked' ? UI.dim : save.coins >= price ? UI.accent : UI.dim,
        ).setOrigin(0.5, 0);
        if (this.unseen.includes(`boost:${b}`)) this.newTag(x, y);
      },
    );
    return {
      x: 10 + PREVIEW_PATCH.width / 2,
      y: CONTENT_Y + PICTURE_DY + PREVIEW_PATCH.height / 2,
    };
  }

  private drawStyleTab(
    g: Phaser.GameObjects.Graphics,
    width: number,
    bottom: number,
    focus: Focus,
  ): { x: number; y: number } {
    const save = saves().save;
    const owned = new Set(save.cosmetics.owned);
    const mower = save.selectedMower;
    // The crown's paint is won online: shown only where online play is (or once owned).
    const list = COSMETICS.filter((x) => !x.crown || ONLINE_ENABLED || owned.has(x.id));
    const c = list.find((x) => focus.kind === 'cosmetic' && x.id === focus.id) ?? list[0]!;
    const has = owned.has(c.id);
    const on = isEquipped(save, c.id, mower);
    // Unfound cosmetics stay a mystery (like locked mowers): no name, no look.
    const panelBottom = this.showcasePanel(
      g,
      width,
      has ? c.name.toUpperCase() : '???',
      { text: on ? 'ON' : has ? 'OWNED' : 'NOT FOUND', color: has ? UI.good : UI.dim },
      list.map((x) => this.cosmeticLines(x)),
    );
    const sy = CONTENT_Y + PICTURE_DY;
    const lines = this.cosmeticLines(c);
    if (isPaint(c.id)) {
      mowerPreview(this, 10, sy, width - 20, mower, {
        texture: paintedMowerTexture(this, mower, c.id),
        lines,
        locked: !has,
      });
    } else {
      stripePreview(this, 10, sy, width - 20, c.id, lines, !has);
    }
    const end = this.grid(
      panelBottom + 8,
      bottom,
      width,
      'COSMETICS',
      list,
      list.indexOf(c),
      (x, i, cx, cy, w) => this.drawCosmeticChip(x, i, cx, cy, w, c.id === x.id),
    );
    // The Supporter Pack where there is room under the grid.
    if (end + 30 < bottom) {
      g.fillStyle(0x2a2550).fillRect(4, end + 4, width - 8, 26);
      g.lineStyle(1, UI.accent).strokeRect(4.5, end + 4.5, width - 9, 25);
      label(this, 10, end + 8, 'SUPPORTER PACK', 1, UI.accent);
      label(this, 10, end + 18, 'Coming soon', 1, UI.dim);
    }
    return { x: 10 + PREVIEW_PATCH.width / 2, y: sy + PREVIEW_PATCH.height / 2 };
  }

  private drawCosmeticChip(
    c: CosmeticDef,
    i: number,
    x: number,
    y: number,
    w: number,
    focused: boolean,
  ): void {
    const save = saves().save;
    const mine = save.cosmetics.owned.includes(c.id);
    const cg = this.chip(x, y, w, CHIP_H, focused, false, mine ? c.name : `??? ${i + 1}`, () =>
      this.setFocus({ kind: 'cosmetic', id: c.id }),
    );
    if (isPaint(c.id)) {
      const img = this.add.image(
        x + w / 2,
        y + 15,
        paintedMowerTexture(this, save.selectedMower, c.id),
        Dir.E,
      );
      centerMowerIcon(img, save.selectedMower);
      if (!mine) img.setTint(UI.shadow).setTintMode(Phaser.TintModes.FILL).setAlpha(0.8);
    } else if (!mine) {
      // An unfound pattern: a blank swatch with a question mark.
      cg.fillStyle(0x2a2550).fillRect(x + Math.round(w / 2) - 14, y + 5, 28, 21);
      label(this, x + w / 2, y + 12, '?', 1, UI.dim).setOrigin(0.5, 0);
    } else {
      // A 4 x 3 swatch of the stripe pattern.
      for (let tx = 0; tx < 4; tx++) {
        for (let ty = 0; ty < 3; ty++) {
          cg.fillStyle(STRIPE_SWATCH[stripeFrame(c.id, tx, ty)] ?? 0x27702e).fillRect(
            x + Math.round(w / 2) - 14 + tx * 7,
            y + 5 + ty * 7,
            7,
            7,
          );
        }
      }
    }
    const worn = isEquipped(save, c.id, save.selectedMower);
    label(
      this,
      x + w / 2,
      y + 31,
      worn ? 'ON' : mine ? 'OWN' : '???',
      1,
      worn ? UI.accent : mine ? UI.good : UI.dim,
    ).setOrigin(0.5, 0);
  }

  // ── Shared pieces ────────────────────────────────────────────────────────────────────────

  /** Pops a chip in (drawn since `mark`), one after another, when the list first appears. */
  private popIn(mark: number, order: number): void {
    if (this.entering) arriveSince(this, mark, 0, 8, 40 + order * 45, 240);
  }

  /** A selectable chip: navy box, yellow frame when focused, tap to focus. Returns its graphics. */
  private chip(
    x: number,
    y: number,
    w: number,
    h: number,
    focused: boolean,
    locked: boolean,
    name: string,
    onTap: () => void,
  ): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();
    g.fillStyle(UI.shadow).fillRect(x - 1, y - 1, w + 2, h + 2);
    g.fillStyle(locked ? 0x1a1538 : UI.panel).fillRect(x, y, w, h);
    g.lineStyle(1, focused ? UI.accent : UI.panelHi).strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    this.add
      .zone(x, y, w, h)
      .setOrigin(0)
      .setData('label', name)
      .setInteractive({ useHandCursor: true })
      .on('pointerup', (p?: Phaser.Input.Pointer) => {
        if (p && p.getDistance() > 8) return; // a swipe across the grid, not a tap
        onTap();
      });
    return g;
  }

  /** The focused item's action as the wide button above the tab bar (or why it is not there). */
  private drawAction(focus: Focus, x: number, y: number, w: number): void {
    const save = saves().save;
    const disabled = (text: string) =>
      label(this, x + w / 2, y + 9, text, 1, UI.dim).setOrigin(0.5, 0);
    const buy = (text: string, cost: number, onPress: () => void) => {
      const afford = save.coins >= cost;
      bevelButton(this, x, y, w, 26, text, onPress, afford, afford ? 2 : 1);
    };
    switch (focus.kind) {
      case 'mower': {
        const id = focus.id;
        const status = mowerStatus(save, id);
        if (save.selectedMower === id) disabled(`${MOWER_DEFS[id].name.toUpperCase()} IN USE`);
        else if (status === 'owned')
          bevelButton(this, x, y, w, 26, 'USE', () => this.select(id), true, 2);
        else if (status === 'buyable') {
          const afford = canBuyMower(save, id).ok;
          bevelButton(
            this,
            x,
            y,
            w,
            26,
            `BUY ${MOWER_DEFS[id].price}`,
            () => this.buyMower(id),
            afford,
            afford ? 2 : 1,
          );
        } else if (waitsForFullGame(save, id)) {
          bevelButton(this, x, y, w, 26, 'FULL GAME', () =>
            wipeTo(this, 'Unlock', { backTo: 'Garage' }),
          );
        } else disabled(`AFTER LAWN ${MOWER_DEFS[id].unlockLevel}`);
        return;
      }
      case 'upgrade': {
        const { mower, id: kind } = focus;
        // A Tuned Engine that is bought: a switch between stock and each level, the next to buy
        // beside them.
        if (kind === 'engine' && upgradeLevels(save, mower).engine > 0) {
          this.drawEngineSwitch(mower, x, y, w);
          return;
        }
        const cost = nextUpgradeCost(save, mower, kind);
        const opensAfter = nextUpgradeOpensAfter(save, mower, kind);
        if (cost === null) {
          disabled(opensAfter !== null ? `AFTER LAWN ${opensAfter}` : 'MAXED OUT');
        } else {
          const afford = save.coins >= cost;
          bevelButton(
            this,
            x,
            y,
            w,
            26,
            `UPGRADE ${cost}`,
            () => this.upgrade(mower, kind),
            afford,
          );
        }
        return;
      }
      case 'tool': {
        const id = focus.id;
        const status = toolStatus(save, id);
        if (status === 'owned') {
          const equipped = save.tools.equipped.includes(id);
          // Beside it, its next upgrade: on sale, or when it opens.
          const up = nextToolUpgrade(save, id);
          const ew = up ? Math.floor((w - 4) / 2) : w;
          bevelButton(
            this,
            x,
            y,
            ew,
            26,
            equipped ? 'UNEQUIP' : 'EQUIP',
            () => this.equip(id),
            !equipped,
            equipped ? 1 : 2,
          );
          if (up) {
            const ux = x + ew + 4;
            const uw = w - ew - 4;
            if (!up.open) {
              label(this, ux + uw / 2, y + 9, `AFTER ${up.opensAfter}`, 1, UI.dim).setOrigin(
                0.5,
                0,
              );
            } else {
              const afford = save.coins >= up.price;
              bevelButton(
                this,
                ux,
                y,
                uw,
                26,
                `UP ${up.price}`,
                () => this.upgradeTool(id),
                afford,
              );
            }
          }
        } else if (status === 'buyable')
          buy(`BUY ${TOOL_BY_ID[id].price}`, TOOL_BY_ID[id].price, () => this.buyTool(id));
        else disabled(`AFTER LAWN ${TOOL_BY_ID[id].unlockLevel}`);
        return;
      }
      case 'boost': {
        const def = BOOST_BY_ID[focus.id];
        const status = boostStatus(save, def.id);
        if (status === 'locked') disabled(`AFTER LAWN ${def.unlockLevel}`);
        else if (status === 'full') disabled('STOCK FULL');
        else buy(`BUY ${def.price}`, def.price, () => this.buyBoost(def.id));
        return;
      }
      case 'cosmetic': {
        const c = COSMETICS.find((x) => x.id === focus.id)!;
        if (!save.cosmetics.owned.includes(c.id)) {
          disabled(cosmeticGoal(c));
          return;
        }
        const on = isEquipped(save, c.id, save.selectedMower);
        // A paint can go on every owned mower at once (owner, 2026-11-04).
        const all = c.slot === 'paint' && ownedMowerIds(save).length > 1;
        const ew = all ? Math.floor((w - 4) / 2) : w;
        bevelButton(
          this,
          x,
          y,
          ew,
          26,
          on ? 'TAKE OFF' : 'EQUIP',
          () => this.wear(c),
          !on,
          on ? 1 : 2,
        );
        if (all) {
          const everywhere = paintOnAllMowers(save, c.id);
          bevelButton(
            this,
            x + ew + 4,
            y,
            w - ew - 4,
            26,
            everywhere ? 'OFF ALL' : 'ALL MOWERS',
            () => this.wearOnAll(c),
          );
        }
        return;
      }
    }
  }

  /**
   * TRY IT under the showcase picture, for an item with a practice lawn (owner, 2026-10-23): a
   * small lawn played free with just that item, owned or not, then back here.
   */
  private tryItButton(item: PracticeItem, open: GarageOpen): void {
    const practice = practiceFor(item);
    if (!practice) return;
    bevelButton(
      this,
      10,
      CONTENT_Y + PICTURE_DY + PREVIEW_PATCH.height + 4,
      PREVIEW_PATCH.width,
      TRY_H,
      'TRY IT',
      () => {
        const data: LevelSceneData = {
          level: practice.level,
          mowerId: practice.mower,
          practice: { item: practice.item, hint: practice.hint },
          playlist: {
            kind: 'test',
            short: 'Try',
            levels: [practice.level],
            index: 0,
            back: { scene: 'Garage', label: 'Garage', data: { open } },
          },
        };
        wipeTo(this, 'Level', data);
      },
    );
  }

  // ── Actions ──────────────────────────────────────────────────────────────────────────────

  private select(id: MowerId): void {
    saves().update((s) => (s.selectedMower = id));
    this.message = `${MOWER_DEFS[id].name} ready to mow.`;
    this.redraw();
  }

  private buyMower(id: MowerId): void {
    let result: ReturnType<typeof buyMower> = { ok: false, reason: 'coins' };
    const from = saves().save.coins;
    saves().update((s) => (result = buyMower(s, id)));
    if (result.ok) this.purchase = { from, stamp: 'YOURS!' };
    this.message = result.ok ? `${MOWER_DEFS[id].name} is yours!` : 'Not enough coins yet.';
    this.redraw();
  }

  private upgrade(id: MowerId, kind: UpgradeKind): void {
    let ok = false;
    const from = saves().save.coins;
    saves().update((s) => (ok = buyUpgrade(s, id, kind)));
    if (ok) this.purchase = { from, stamp: 'UPGRADED!' };
    this.message = ok ? `${UPGRADES[kind].name} upgraded!` : 'Not enough coins yet.';
    this.redraw();
  }

  /**
   * The Tuned Engine's switch (owner, 2026-10-29: one button that stepped down and wrapped round
   * did not say what was in use): STOCK and each level bought, the one in use lit; a level not
   * bought yet is its BUY button (or when it opens).
   */
  private drawEngineSwitch(id: MowerId, x: number, y: number, w: number): void {
    const save = saves().save;
    const levels = upgradeCosts(id, 'engine').length;
    const bought = upgradeLevels(save, id).engine;
    const inUse = engineInUse(save, id);
    const gap = 3;
    const segW = Math.floor((w - gap * levels) / (levels + 1));
    for (let level = 0; level <= levels; level++) {
      const sx = x + level * (segW + gap);
      // The last segment takes what rounding left over.
      const sw = level === levels ? x + w - sx : segW;
      if (level <= bought) {
        const name = level === 0 ? 'STOCK' : `TUNED ${level}`;
        bevelButton(this, sx, y, sw, 26, name, () => this.setEngine(id, level), level === inUse);
        continue;
      }
      const cost = level === bought + 1 ? nextUpgradeCost(save, id, 'engine') : null;
      if (cost !== null) {
        bevelButton(this, sx, y, sw, 26, `BUY ${cost}`, () => this.upgrade(id, 'engine'));
      } else {
        const opens = level === bought + 1 ? nextUpgradeOpensAfter(save, id, 'engine') : null;
        label(this, sx + sw / 2, y + 9, opens !== null ? `LAWN ${opens}` : '', 1, UI.dim).setOrigin(
          0.5,
          0,
        );
      }
    }
  }

  /** Sets the Tuned Engine to a level bought (0: stock). */
  private setEngine(id: MowerId, level: number): void {
    saves().update((s) => setEngine(s, id, level));
    const inUse = engineInUse(saves().save, id);
    this.message = inUse === 0 ? 'Engine at stock speed.' : `Engine tuned to level ${inUse}.`;
    this.redraw();
  }

  private buyTool(id: ToolId): void {
    let result: ReturnType<typeof buyTool> = { ok: false, reason: 'coins' };
    const from = saves().save.coins;
    saves().update((s) => (result = buyTool(s, id)));
    if (result.ok) this.purchase = { from, stamp: 'YOURS!' };
    this.message = result.ok ? `${TOOL_BY_ID[id].name} is yours!` : 'Not enough coins yet.';
    this.redraw();
  }

  private upgradeTool(id: ToolId): void {
    let result: ReturnType<typeof buyToolUpgrade> = { ok: false, reason: 'coins' };
    const from = saves().save.coins;
    saves().update((s) => (result = buyToolUpgrade(s, id)));
    if (result.ok) this.purchase = { from, stamp: 'UPGRADED!' };
    this.message = result.ok ? `${TOOL_BY_ID[id].name} upgraded!` : 'Not enough coins yet.';
    this.redraw();
  }

  private equip(id: ToolId): void {
    saves().update((s) => toggleEquip(s, id));
    this.redraw();
  }

  private buyBoost(id: BoostId): void {
    let result: ReturnType<typeof buyBoost> = { ok: false, reason: 'coins' };
    const from = saves().save.coins;
    saves().update((s) => (result = buyBoost(s, id)));
    if (result.ok) this.purchase = { from, stamp: '+1' };
    this.message = result.ok ? `${BOOST_BY_ID[id].name} added!` : 'Not enough coins yet.';
    this.redraw();
  }

  /** Puts a paint on every owned mower, or takes it off them all when they all wear it. */
  private wearOnAll(c: CosmeticDef): void {
    saves().update((s) => paintAllMowers(s, c.id));
    this.message = paintOnAllMowers(saves().save, c.id)
      ? 'Every mower repainted!'
      : 'Back to the standard look.';
    this.redraw();
  }

  /** Puts a cosmetic on (a paint: on the mower in use), or takes it off. */
  private wear(c: CosmeticDef): void {
    const mower = saves().save.selectedMower;
    saves().update((s) => toggleCosmetic(s, c.id, mower));
    const on = isEquipped(saves().save, c.id, mower);
    this.message = on
      ? c.slot === 'paint'
        ? `${MOWER_DEFS[mower].name} repainted!`
        : 'New stripes on every lawn!'
      : 'Back to the standard look.';
    this.redraw();
  }
}

/** How a cosmetic is won, as a short line: the goal while it is locked, the deed once owned. */
function cosmeticSource(c: CosmeticDef, owned: boolean): string {
  const zone = BIOMES.find((b) => b.id === c.zone)?.name ?? '';
  if (c.zone) return owned ? `Won for every star in ${zone}.` : `Every star in ${zone}.`;
  if (c.gift) return owned ? 'Thanks for buying the full game.' : 'Comes with the full game.';
  if (c.critter) return owned ? 'Won for tagging a critter.' : 'Tag a squirrel or a rabbit.';
  if (c.share)
    return owned ? `Won for ${SHARE_GOAL} shares.` : `Share the game ${SHARE_GOAL} times.`;
  if (c.crown)
    return owned ? 'Won for a week first on the board.' : 'End a week first on the weekly board.';
  if (c.grand) return owned ? 'Won for every star in the game.' : 'Every star in the game.';
  if (c.yards) return owned ? 'Won for every secret yard.' : 'A secret.';
  if (c.cleared) return owned ? 'Won for mowing every lawn.' : 'Mow every lawn in the game.';
  return owned ? 'A rare find from a lawn.' : 'A rare find, out on a lawn.';
}

/** A locked cosmetic's goal, on the disabled action button. */
function cosmeticGoal(c: CosmeticDef): string {
  const zone = BIOMES.find((b) => b.id === c.zone)?.name;
  if (zone) return `ALL STARS: ${zone.toUpperCase()}`;
  if (c.gift) return 'IN THE FULL GAME';
  if (c.critter) return 'TAG A CRITTER';
  if (c.share) return 'TELL A FRIEND';
  if (c.crown) return 'WIN A WEEKLY LAWN';
  if (c.grand) return 'EVERY STAR IN THE GAME';
  if (c.yards) return 'A SECRET';
  if (c.cleared) return 'MOW EVERY LAWN';
  return 'FIND IT ON A LAWN';
}
